/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Assignment List
 *
 * @file AssignmentList.jsx
 * @author Gladson Samuel S
 * @since 2.0.0
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Typography } from '@mui/material';
import Icon from '../icons/Icon';
import AssignmentListItem, {
  defaultConfig as listItemDefaultConfig
} from './AssignmentListItem';
import AssignmentDueDateModalProvider from './AssignmentDueDateModalProvider';
import { AssignmentsStylesRoot, classes } from './styles/assignmentsStyles';

export const listItemConfig = {
  showDate: true,
  showSections: true,
  showMenu: true
};

function isDueDateModalConfigEnabled(config = {}) {
  return Boolean(config.showAddDatePlaceholder || config.showDueDateEditableIcon);
}

const AssignmentList = ({
  assignments,
  textInfo,
  selectedAssignmentId,
  listItemConfig: listItemConfigProp,
  onSelect,
  onCheckboxChange,
  onEdit,
  onDueDateClick,
  onDueDateSave,
  onContentDrop,
  onSectionRemove,
  onDeselect,
  onRemove,
  onTitleChange
}) => {
  const itemConfig = { ...listItemDefaultConfig, ...listItemConfig, ...listItemConfigProp };
  const shouldEnableDueDateModal = isDueDateModalConfigEnabled(itemConfig);
  const isScheduleBuilder = itemConfig.scheduleBuilderLayout;
  const listPaneClassName = isScheduleBuilder ? classes.scheduleListPane : classes.listPane;
  const assignmentListClassName = isScheduleBuilder
    ? classes.scheduleAssignmentList
    : classes.assignmentList;

  const renderEmptyState = () => (
    <div className={classes.emptyState}>
      <div className={classes.emptyStateIcon}>
        <Icon name="ClipboardFileIcon" />
      </div>
      <Typography className={classes.emptyStateTitle} component="p">
        {textInfo.emptyStateTitle}
      </Typography>
      <Typography className={classes.emptyStateDescription} component="p">
        {textInfo.emptyStateDescription}
      </Typography>
    </div>
  );

  const renderAssignmentItems = () => (
    assignments.map((assignment) => (
      <AssignmentListItem
        key={assignment.id}
        assignment={assignment}
        isSelected={assignment.id === selectedAssignmentId}
        textInfo={textInfo}
        config={itemConfig}
        onSelect={onSelect}
        onCheckboxChange={onCheckboxChange}
        onEdit={onEdit}
        onDueDateClick={onDueDateClick}
        onContentDrop={onContentDrop}
        onSectionRemove={onSectionRemove}
        onDeselect={onDeselect}
        onRemove={onRemove}
        onTitleChange={onTitleChange}
      />
    ))
  );

  const renderListContent = () => {
    if (!assignments.length) {
      return (
        <AssignmentsStylesRoot className={classes.emptyStateContainer}>
          {renderEmptyState()}
        </AssignmentsStylesRoot>
      );
    }

    return (
      <AssignmentsStylesRoot className={listPaneClassName}>
        <div className={assignmentListClassName}>
          {renderAssignmentItems()}
        </div>
      </AssignmentsStylesRoot>
    );
  };

  if (shouldEnableDueDateModal) {
    return (
      <AssignmentDueDateModalProvider
        textInfo={textInfo}
        onDueDateSave={onDueDateSave}
      >
        {renderListContent()}
      </AssignmentDueDateModalProvider>
    );
  }

  return renderListContent();
};

AssignmentList.propTypes = {
  assignments: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    date: PropTypes.string,
    time: PropTypes.string,
    sections: PropTypes.arrayOf(PropTypes.string),
    checked: PropTypes.bool,
    menuItems: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func,
      divider: PropTypes.bool
    }))
  })),
  textInfo: PropTypes.object.isRequired,
  selectedAssignmentId: PropTypes.string,
  listItemConfig: PropTypes.object,
  onSelect: PropTypes.func,
  onCheckboxChange: PropTypes.func,
  onEdit: PropTypes.func,
  onDueDateClick: PropTypes.func,
  onDueDateSave: PropTypes.func,
  onContentDrop: PropTypes.func,
  onSectionRemove: PropTypes.func,
  onDeselect: PropTypes.func,
  onRemove: PropTypes.func,
  onTitleChange: PropTypes.func
};

AssignmentList.defaultProps = {
  assignments: [],
  selectedAssignmentId: '',
  listItemConfig,
  onSelect: null,
  onCheckboxChange: null,
  onEdit: null,
  onDueDateClick: null,
  onDueDateSave: null,
  onContentDrop: null,
  onSectionRemove: null,
  onDeselect: null,
  onRemove: null,
  onTitleChange: null
};

export default AssignmentList;
